'use client';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Brush2 } from 'iconsax-react';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import Label from '@/components/atoms/Label';
import FormField from '@/components/FormField/FormField';
import classNames from 'classnames';
import { Skeleton } from 'antd';
import type { UploadFile } from 'antd';
import type { AllowedFileTypes } from '@/components/FormField/interfaces';
import { useUploadProductLogoByAdminMutation } from '../../../../../../store/services/products';
import { useAddProductLookups } from '../_context/AddProductLookupsContext';

interface UploadResponse {
  payload: {
    id: string;
    blobFileUrl: string;
    blobName?: string;
    blobSize?: number;
    extension?: string;
    name?: string;
  };
}

const OemCustomizationForm = () => {
  const { t, i18n } = useTranslation();
  const { t: tImage } = useTranslation();
  const { t: tComponents } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  const [logoLoading, setLogoLoading] = useState(false);

  const [uploadLogo_] = useUploadProductLogoByAdminMutation();
  const uploadLogoApi = async (formData: FormData) => {
    return await uploadLogo_(formData).unwrap();
  };

  const { customizationTypes: supportTypes, customizationTypesLoading: supportTypesLoading } = useAddProductLookups();

  const customizationTypes: string[] = watch('customizationTypes') ?? [];
  const customLogoId = supportTypes.find((t) => t.code === 'CUSTOM_LOGO')?.id;
  const hasCustomLogo = customLogoId ? customizationTypes.includes(customLogoId) : false;

  React.useEffect(() => {
    setValue('hasCustomLogo', hasCustomLogo);
  }, [hasCustomLogo, setValue]);

  const uploadLogo = async () => {
    if (logoLoading) return; // prevent concurrent calls
    const currentList = (watch('customLogo') || []) as UploadFile[];
    const newFiles = currentList.filter((f) => f.originFileObj && !f.url);
    if (newFiles.length === 0) return;

    const updatedWithUploading = currentList.map((file) => {
      if (newFiles.includes(file)) {
        return { ...file, status: 'uploading' as const, uploading: true };
      }
      return file;
    });
    setValue('customLogo', updatedWithUploading);
    setLogoLoading(true);

    try {
      const formData = new FormData();
      newFiles.forEach((file) => {
        if (file.originFileObj) {
          formData.append('file', file.originFileObj);
        }
      });

      const response = await uploadLogoApi(formData);
      if (response?.payload?.id) {
        const item = response.payload;
        const updatedList = updatedWithUploading.map((file) => {
          if (file.originFileObj && !file.url) {
            return {
              ...file,
              uid: item.id,
              url: item.blobFileUrl,
              name: item.blobName || item.name || file.name,
              status: 'done' as const,
              uploading: false,
            };
          }
          return file;
        });
        setValue('customLogo', updatedList, { shouldDirty: true, shouldValidate: true });
      } else {
        throw new Error('Invalid response');
      }
    } catch {
      const updatedList = currentList.filter((f) => !newFiles.includes(f));
      setValue('customLogo', updatedList, { shouldDirty: true, shouldValidate: true });
    } finally {
      setLogoLoading(false);
    }
  };

  const imgAllowedTypes: AllowedFileTypes[] = [
    'image/jpeg',
    'image/png',
    'image/webp',
  ] as AllowedFileTypes[];

  return (
    <div
      className="bg-white dark:bg-dark-surface-primary p-4 md:p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border shadow-sm mb-8 animate-fade-in"
      data-testid="oem-customization-card"
    >
      {/* Header */}
      <div className="mb-6 flex gap-4 items-center" data-testid="oem-header">
        <div
          className="p-3 bg-soft-background dark:bg-dark-button-bg-secondary rounded-xl text-neutral-900 dark:text-dark-text-primary flex items-center justify-center"
          data-testid="oem-icon-container"
        >
          <Brush2 variant="Bold" size={24} />
        </div>
        <div data-testid="oem-header-text">
          <TitleText
            text={t('oemSectionTitle')}
            size="lg"
            className="!font-bold !text-[18px] text-[#090909] dark:text-dark-text-primary !leading-[100%] !tracking-[0px]"
          />
          <InfoText
            text={t('logisticsDataDesc')}
            size="xs"
            className="!font-normal !text-[12px] text-[#777777] dark:text-dark-text-secondary !leading-[100%] !tracking-[0px] mt-1"
          />
        </div>
      </div>

      <div className="flex flex-col gap-6" data-testid="oem-fields">

        {/* Support Types */}
        <div data-testid="support-types-container">
          <Label
            text={t('supportTypes')}
            className="text-sm !font-semibold text-light-text-primary dark:text-dark-text-primary"
            id="support-types-label"
          />
          <Controller
            control={control}
            name="customizationTypes"
            render={({ field: { value = [], onChange } }) => {
              const handleToggle = (key: string) => {
                const isCurrentlySelected = (value as string[]).includes(key);
                const next: string[] = isCurrentlySelected
                  ? (value as string[]).filter((v) => v !== key)
                  : [...(value as string[]), key];
                onChange(next);
                // Clear logo files when CUSTOM_LOGO is deselected
                if (key === customLogoId && isCurrentlySelected) {
                  setValue('customLogo', [], { shouldDirty: true, shouldValidate: true });
                }
              };

              return (
                <div
                  className="flex flex-wrap gap-3 mt-3"
                  data-testid="support-types-options"
                >
                  {supportTypesLoading ? (
                    <Skeleton active paragraph={{ rows: 2 }} title={false} />
                  ) : (
                    supportTypes.map((item) => {
                      const isSelected = (value as string[]).includes(
                        item.id
                      );
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleToggle(item.id)}
                          className={classNames(
                            'px-4 py-2 rounded-lg text-sm !font-normal leading-none transition-all duration-200 border',
                            isSelected
                              ? 'bg-light-success dark:bg-dark-success border-light-success dark:border-dark-success text-white shadow-sm'
                              : 'bg-white dark:bg-dark-surface-secondary border-light-input-border dark:border-dark-input-border !text-light-text-primary dark:!text-dark-text-primary hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          )}
                          data-testid={`support-type-${item.code}`}
                        >
                          {(() => {
                            const mapping: Record<string, Parameters<typeof t>[0]> = {
                              CUSTOM_LOGO: 'customLogo',
                              CUSTOM_COLOR: 'customColor',
                              CUSTOM_PACKAGING: 'customPackaging',
                              OEM_YOUR_DESIGN: 'oemYourDesign',
                              PRIVATE_LABEL: 'privateLabelYourText',
                              CUSTOM_SIZE: 'customSize',
                            };
                            // Also try camelCase fallback (e.g. OEM -> oem)
                            const camelKey = item.code.toLowerCase().replace(/_([a-z])/g, (g) => g[1].toUpperCase()) as Parameters<typeof t>[0];
                            const key = mapping[item.code] || camelKey;
                            return i18n.exists(key as string) ? t(key) : item.name;
                          })()}
                        </button>
                      );
                    })
                  )}
                </div>
              );
            }}
          />
        </div>

        {/* Custom Logo Upload — only when customLogo is selected */}
        {hasCustomLogo && (
          <div id="customLogo" data-testid="custom-logo-area-container">
            <FormField
              type="file"
              name="customLogo"
              id="customLogo-input"
              label={t('customLogoArea')}
              maxCount={1}
              allowedTypes={imgAllowedTypes}
              uploadHints={[
                tImage('dropzoneAcceptedTypes', { types: '[list of file types, e.g., .jpg, .png.]' }),
                tComponents('components.maxFiles', { filesNum: 1, fileSize: 5 }),
              ]}
              maxFileSize={5}
              multiple={false}
              uploadFileRequest={uploadLogo}
              loading={logoLoading}
              uploadDescription={tImage('dropzoneHeading')}
              labelHelperText={`${(watch('customLogo') || []).length}/1 ${tImage('imagesTitle').toLowerCase()}`}
            />
          </div>
        )}

      </div>

    </div>
  );
};

export default OemCustomizationForm;
